import React, { useCallback, useEffect, useState } from "react";
import { StarRating } from "@/components/StarRating";
import { Eye, EyeOff, Trash2, Loader2, MessageSquare, User, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("hu-HU", {
      year: "numeric", month: "long", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

// Egyszerű heurisztikus spam-jelzés
const spamScore = (r) => {
  let score = 0;
  const t = (r.text || "").toLowerCase();
  if (t.length < 15) score += 2;
  if (/https?:\/\//i.test(t)) score += 2;
  if (/(viagra|casino|kripto|bitcoin|free\s+money|forex)/i.test(t)) score += 3;
  const alpha = t.replace(/[^a-záéíóöőúüű]/gi, "").length;
  if (t.length > 0 && alpha / t.length < 0.4) score += 1;
  if (/(.)\1{5,}/.test(t)) score += 2;
  return score;
};

const FILTERS = [
  { key: "all", label: "Összes" },
  { key: "visible", label: "Látható" },
  { key: "hidden", label: "Rejtett" },
  { key: "flagged", label: "Gyanús" },
];

export function AdminReviewsPanel() {
  const [reviews, setReviews] = useState([]);
  const [counts, setCounts] = useState({ total: 0, visible: 0, hidden: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/site-reviews");
      setReviews(r.data.reviews || []);
      setCounts(r.data.counts || { total: 0, visible: 0, hidden: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Betöltés sikertelen");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flagged = reviews.filter((r) => !r.is_deleted && spamScore(r) >= 2);

  const filtered = reviews.filter((r) => {
    if (filter === "visible") return !r.is_deleted;
    if (filter === "hidden") return r.is_deleted;
    if (filter === "flagged") return !r.is_deleted && spamScore(r) >= 2;
    return true;
  });

  const hide = async (r) => {
    setBusyId(r.id);
    try {
      await api.post(`/admin/site-reviews/${r.id}/hide`);
      toast.success("Vélemény elrejtve");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Elrejtés sikertelen");
    } finally { setBusyId(null); }
  };

  const restore = async (r) => {
    setBusyId(r.id);
    try {
      await api.post(`/admin/site-reviews/${r.id}/restore`);
      toast.success("Vélemény visszaállítva");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Visszaállítás sikertelen");
    } finally { setBusyId(null); }
  };

  const del = async (r) => {
    if (!window.confirm(`Véglegesen törlöd "${r.author_name}" véleményét? Ez nem visszavonható.`)) return;
    setBusyId(r.id);
    try {
      await api.delete(`/admin/site-reviews/${r.id}`);
      toast.success("Vélemény törölve");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Törlés sikertelen");
    } finally { setBusyId(null); }
  };

  return (
    <div data-testid="admin-reviews-panel" className="space-y-6">
      {/* KPI-k */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Összes" value={counts.total} testid="admin-reviews-kpi-total" />
        <Kpi label="Látható" value={counts.visible} color="#22C55E" testid="admin-reviews-kpi-visible" />
        <Kpi label="Rejtett" value={counts.hidden} color="#94A3B8" testid="admin-reviews-kpi-hidden" />
        <Kpi label="Gyanús" value={flagged.length} color="#F59E0B" testid="admin-reviews-kpi-flagged" />
      </div>

      {/* Filter + refresh */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-full border border-white/10 p-0.5">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`admin-reviews-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`px-3.5 py-1.5 rounded-full text-xs transition-colors ${
                filter === f.key ? "bg-electric text-white" : "text-slate-400 hover:text-white"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button
          data-testid="admin-reviews-refresh"
          onClick={load}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full glass hover:bg-white/10 text-xs text-slate-300"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Frissítés
        </button>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-electric animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="glass rounded-2xl p-8 text-center text-slate-400 text-sm" data-testid="admin-reviews-empty">
          Ebben a nézetben nincs vélemény.
        </div>
      ) : (
        <ul className="space-y-3" data-testid="admin-reviews-list">
          {filtered.map((r) => {
            const isFlagged = !r.is_deleted && spamScore(r) >= 2;
            return (
              <li
                key={r.id}
                data-testid={`admin-review-item-${r.id}`}
                className={`glass rounded-2xl p-4 md:p-5 ${r.is_deleted ? "opacity-60" : ""}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="w-9 h-9 rounded-full bg-electric/15 flex items-center justify-center text-electric shrink-0">
                      <User className="w-4 h-4" strokeWidth={1.75} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-medium truncate" data-testid="admin-review-author">{r.author_name}</div>
                        <StarRating value={r.rating} size="xs" />
                        {r.is_deleted && (
                          <span data-testid="admin-review-hidden-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-500/20 text-slate-300 text-[10px] uppercase tracking-wider">
                            <EyeOff className="w-2.5 h-2.5" /> Rejtett
                          </span>
                        )}
                        {isFlagged && (
                          <span data-testid="admin-review-flagged-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-[10px] uppercase tracking-wider">
                            <AlertTriangle className="w-2.5 h-2.5" /> Gyanús
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">{formatDate(r.created_at)}</div>
                      <p className="text-sm text-slate-300 leading-relaxed mt-2 whitespace-pre-wrap break-words" data-testid="admin-review-body">
                        {r.text}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {r.is_deleted ? (
                      <button
                        data-testid={`admin-review-restore-${r.id}`}
                        disabled={busyId === r.id}
                        onClick={() => restore(r)}
                        title="Visszaállítás"
                        className="w-9 h-9 rounded-full glass hover:bg-emerald-500/15 hover:text-emerald-400 flex items-center justify-center text-slate-300 transition-colors"
                      >
                        {busyId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" strokeWidth={1.75} />}
                      </button>
                    ) : (
                      <button
                        data-testid={`admin-review-hide-${r.id}`}
                        disabled={busyId === r.id}
                        onClick={() => hide(r)}
                        title="Elrejtés"
                        className="w-9 h-9 rounded-full glass hover:bg-amber-500/15 hover:text-amber-400 flex items-center justify-center text-slate-300 transition-colors"
                      >
                        {busyId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <EyeOff className="w-4 h-4" strokeWidth={1.75} />}
                      </button>
                    )}
                    <button
                      data-testid={`admin-review-delete-${r.id}`}
                      disabled={busyId === r.id}
                      onClick={() => del(r)}
                      title="Végleges törlés"
                      className="w-9 h-9 rounded-full glass hover:bg-red-500/15 hover:text-red-400 flex items-center justify-center text-slate-300 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

const Kpi = ({ label, value, color = "#2563EB", testid }) => (
  <div className="glass rounded-2xl p-4 flex items-center gap-3" data-testid={testid}>
    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${color}22` }}>
      <MessageSquare className="w-4 h-4" strokeWidth={1.5} style={{ color }} />
    </div>
    <div className="min-w-0">
      <div className="font-heading text-xl font-light">{value}</div>
      <div className="text-xs text-slate-400">{label}</div>
    </div>
  </div>
);
